'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { EventChip } from '../event-chip/index.js';
import { allDaySpan, daySpan, defaultZone, formatDay, formatMinutes, formatTime, packColumns, wallTimeToInstant, weekday, zonedParts, } from './time-grid.dates.js';
const DRAG_THRESHOLD = 4;
function toDate(v) {
    return v instanceof Date ? v : new Date(v);
}
/** The current time: injected, or read after mount and refreshed every minute. */
function useNow(now) {
    const injected = now === undefined ? undefined : now === null ? null : toDate(now);
    const [tick, setTick] = useState(null);
    useEffect(() => {
        if (now !== undefined)
            return;
        setTick(new Date());
        const id = window.setInterval(() => setTick(new Date()), 60_000);
        return () => window.clearInterval(id);
    }, [now]);
    return injected === undefined ? tick : injected;
}
/**
 * The day and week view of a calendar: an hour gutter, an all-day row and one
 * column per day, with overlapping events laid out side by side, a now
 * indicator, working-hours shading and click-to-create slots that also work
 * from the keyboard.
 */
export const TimeGrid = forwardRef(function TimeGrid({ days, events = [], timeZone, locale, hourCycle, now: nowProp, startHour = 0, endHour = 24, hourHeight = 48, snapMinutes = 15, workingHours, unavailable = [], scrollToMinutes, onSlotClick, onEventClick, onEventMove, canMoveEvent, onDayClick, renderEvent, showAllDayRow = false, labels, className, style, ...rest }, ref) {
    const zone = timeZone ?? defaultZone();
    const hc = hourCycle === '12' ? 'h12' : hourCycle === '24' ? 'h23' : undefined;
    const now = useNow(nowProp);
    const nowParts = now ? zonedParts(now, zone) : null;
    const rangeStart = Math.max(0, Math.min(23, startHour)) * 60;
    const rangeEnd = Math.max(rangeStart + 60, Math.min(24, endHour) * 60);
    const minutePx = hourHeight / 60;
    const hours = useMemo(() => Array.from({ length: (rangeEnd - rangeStart) / 60 }, (_, i) => rangeStart / 60 + i), [rangeStart, rangeEnd]);
    const columns = useMemo(() => days.map((day) => {
        const timed = packColumns(events.flatMap((e) => {
            if (e.allDay)
                return [];
            const span = daySpan(e.start, e.end, day, zone, snapMinutes);
            if (!span)
                return [];
            const top = Math.max(span.top, rangeStart);
            const bottom = Math.min(span.bottom, rangeEnd);
            return bottom > top ? [{ item: e, top, bottom }] : [];
        })).sort((a, b) => a.top - b.top || a.col - b.col);
        const allDay = events.filter((e) => {
            if (!e.allDay)
                return false;
            const { first, last } = allDaySpan(e, zone);
            return first <= day && day <= last;
        });
        const off = unavailable.flatMap((u) => {
            const span = daySpan(u.start, u.end, day, zone, 1);
            if (!span)
                return [];
            const top = Math.max(span.top, rangeStart);
            const bottom = Math.min(span.bottom, rangeEnd);
            return bottom > top ? [{ u, top, bottom }] : [];
        });
        let shade = [];
        if (workingHours) {
            const w = weekday(day);
            const open = workingHours
                .filter((h) => h.day === w)
                .map((h) => ({ top: h.start, bottom: h.end }))
                .sort((a, b) => a.top - b.top);
            let cursor = rangeStart;
            for (const o of open) {
                if (o.top > cursor)
                    shade.push({ top: cursor, bottom: Math.min(o.top, rangeEnd) });
                cursor = Math.max(cursor, o.bottom);
            }
            if (cursor < rangeEnd)
                shade.push({ top: cursor, bottom: rangeEnd });
            shade = shade.filter((s) => s.bottom > s.top);
        }
        return { day, timed, allDay, off, shade };
    }), [days, events, zone, snapMinutes, rangeStart, rangeEnd, unavailable, workingHours]);
    const hasAllDay = showAllDayRow || columns.some((c) => c.allDay.length > 0);
    /* ---- Scrolling ---------------------------------------------------- */
    const scrollerRef = useRef(null);
    const setRefs = useCallback((el) => {
        scrollerRef.current = el;
        if (typeof ref === 'function')
            ref(el);
        else if (ref)
            ref.current = el;
    }, [ref]);
    const firstTop = Math.min(...columns.flatMap((c) => c.timed.map((t) => t.top)), Infinity);
    const scrollTarget = scrollToMinutes ?? (Number.isFinite(firstTop) ? Math.max(0, firstTop - 60) : 8 * 60);
    const scrollKey = `${days[0] ?? ''}:${days.length}`;
    const scrolledFor = useRef(null);
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || scrolledFor.current === scrollKey)
            return;
        scrolledFor.current = scrollKey;
        // A little above the target, so its hour label is not under the sticky header.
        el.scrollTop = Math.max(0, (scrollTarget - rangeStart) * minutePx - 12);
    }, [scrollKey, scrollTarget, rangeStart, minutePx]);
    /* ---- Keyboard slots ----------------------------------------------- */
    const defaultHour = Math.min(Math.max(Math.ceil(scrollTarget / 60), rangeStart / 60), rangeEnd / 60 - 1);
    const [activeSlot, setActiveSlot] = useState(null);
    const active = {
        dayIndex: Math.min(activeSlot?.dayIndex ?? 0, Math.max(0, days.length - 1)),
        hour: Math.min(Math.max(activeSlot?.hour ?? defaultHour, rangeStart / 60), rangeEnd / 60 - 1),
    };
    const bodyRef = useRef(null);
    const focusSlot = (dayIndex, hour) => {
        setActiveSlot({ dayIndex, hour });
        const btn = bodyRef.current?.querySelector(`[data-slot="${dayIndex}:${hour}"]`);
        btn?.focus();
        btn?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    };
    const onSlotKeyDown = (e, dayIndex, hour) => {
        const rtl = e.currentTarget.closest('[dir="rtl"]') !== null;
        const first = rangeStart / 60;
        const last = rangeEnd / 60 - 1;
        let next = null;
        switch (e.key) {
            case 'ArrowUp':
                next = [dayIndex, Math.max(first, hour - 1)];
                break;
            case 'ArrowDown':
                next = [dayIndex, Math.min(last, hour + 1)];
                break;
            case 'ArrowLeft':
                next = [Math.max(0, Math.min(days.length - 1, dayIndex + (rtl ? 1 : -1))), hour];
                break;
            case 'ArrowRight':
                next = [Math.max(0, Math.min(days.length - 1, dayIndex + (rtl ? -1 : 1))), hour];
                break;
            case 'Home':
                next = e.ctrlKey ? [0, first] : [dayIndex, first];
                break;
            case 'End':
                next = e.ctrlKey ? [days.length - 1, last] : [dayIndex, last];
                break;
            default:
                return;
        }
        e.preventDefault();
        focusSlot(next[0], next[1]);
    };
    const slotFromPointer = (e, day, hour) => {
        let minutes = hour * 60;
        // detail 0 is a keyboard activation: use the slot's own hour.
        if (e.detail !== 0) {
            const column = e.currentTarget.closest('.mn-time-grid__column');
            const rect = column?.getBoundingClientRect();
            if (rect && rect.height > 0) {
                const raw = rangeStart + (e.clientY - rect.top) / minutePx;
                minutes = Math.floor(raw / snapMinutes) * snapMinutes;
                minutes = Math.max(rangeStart, Math.min(rangeEnd - snapMinutes, minutes));
            }
        }
        return { day, minutes, start: wallTimeToInstant(day, minutes, zone) };
    };
    /* ---- Drag to move ------------------------------------------------- */
    const [drag, setDrag] = useState(null);
    const origin = useRef(null);
    const suppressClick = useRef(false);
    const pointerTarget = (clientX, clientY) => {
        const rect = bodyRef.current?.querySelector('.mn-time-grid__columns')?.getBoundingClientRect();
        if (!rect || rect.width === 0)
            return null;
        const rtl = bodyRef.current?.closest('[dir="rtl"]') !== null;
        let dayIndex = Math.floor(((clientX - rect.left) / rect.width) * days.length);
        if (rtl)
            dayIndex = days.length - 1 - dayIndex;
        dayIndex = Math.max(0, Math.min(days.length - 1, dayIndex));
        const minutes = rangeStart + (clientY - rect.top) / minutePx;
        return { dayIndex, minutes };
    };
    const movable = (ev) => !!onEventMove && (canMoveEvent ? canMoveEvent(ev) : true);
    const onEventPointerDown = (e, ev, top) => {
        if (!movable(ev) || e.button !== 0 || e.pointerType === 'touch')
            return;
        const at = pointerTarget(e.clientX, e.clientY);
        if (!at)
            return;
        const start = toDate(ev.start).getTime();
        const end = ev.end === undefined ? start : toDate(ev.end).getTime();
        origin.current = {
            x: e.clientX,
            y: e.clientY,
            grab: at.minutes - top,
            duration: Math.max(snapMinutes, Math.round((end - start) / 60000)),
            moved: false,
            pointerId: e.pointerId,
        };
    };
    const onEventPointerMove = (e, ev) => {
        const o = origin.current;
        if (!o || o.pointerId !== e.pointerId)
            return;
        if (!o.moved) {
            if (Math.hypot(e.clientX - o.x, e.clientY - o.y) < DRAG_THRESHOLD)
                return;
            o.moved = true;
            e.currentTarget.setPointerCapture?.(e.pointerId);
        }
        const at = pointerTarget(e.clientX, e.clientY);
        if (!at)
            return;
        const snapped = Math.round((at.minutes - o.grab) / snapMinutes) * snapMinutes;
        setDrag({
            event: ev,
            duration: o.duration,
            dayIndex: at.dayIndex,
            startMin: Math.max(0, Math.min(1440 - snapMinutes, snapped)),
        });
    };
    const onEventPointerUp = (e) => {
        const o = origin.current;
        origin.current = null;
        if (!o || !o.moved)
            return;
        if (e.currentTarget.hasPointerCapture?.(e.pointerId))
            e.currentTarget.releasePointerCapture(e.pointerId);
        suppressClick.current = true;
        window.setTimeout(() => {
            suppressClick.current = false;
        }, 0);
        const d = drag;
        setDrag(null);
        const day = d ? days[d.dayIndex] : undefined;
        if (!d || !day || !onEventMove)
            return;
        const start = wallTimeToInstant(day, d.startMin, zone);
        if (start.getTime() === toDate(d.event.start).getTime())
            return;
        onEventMove({
            event: d.event,
            start,
            end: new Date(start.getTime() + d.duration * 60000),
        });
    };
    const cancelDrag = () => {
        origin.current = null;
        setDrag(null);
    };
    // Escape cancels a drag wherever focus is (Safari does not focus buttons on click).
    const dragging = drag !== null;
    useEffect(() => {
        if (!dragging)
            return;
        const onKey = (e) => {
            if (e.key !== 'Escape')
                return;
            e.preventDefault();
            origin.current = null;
            setDrag(null);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [dragging]);
    /* ---- Render -------------------------------------------------------- */
    const allDayLabel = labels?.allDay ?? 'All day';
    const createLabel = labels?.createEvent ?? ((when) => `Create event, ${when}`);
    const todayLabel = labels?.today ?? 'Today';
    const openDayLabel = labels?.openDay ?? ((day) => `Open ${day}`);
    const multi = days.length > 1;
    const today = nowParts?.day;
    const longDay = (day) => formatDay(day, { weekday: 'long', day: 'numeric', month: 'long' }, locale);
    const timeText = (v) => formatTime(v, zone, locale, hc);
    const vars = {
        '--mn-time-grid-days': String(days.length),
        '--mn-time-grid-hour': `${hourHeight}px`,
        '--mn-time-grid-height': `${hours.length * hourHeight}px`,
        ...style,
    };
    const chipFor = (ev, variant, heightPx) => {
        const range = ev.end === undefined ? timeText(ev.start) : `${timeText(ev.start)} – ${timeText(ev.end)}`;
        const compact = heightPx < 38;
        const chip = (_jsx(EventChip, { variant: variant, title: ev.title, ...(variant === 'bar'
                ? { timeLabel: allDayLabel }
                : compact
                    ? { time: timeText(ev.start), timeLabel: range, density: 'compact' }
                    : { time: range }), ...(ev.status ? { status: ev.status } : {}), ...(ev.color ? { color: ev.color } : {}), ...(ev.detail && !compact && heightPx >= 56 ? { detail: ev.detail } : {}), ...(ev.href !== undefined ? { href: ev.href } : {}), ...(onEventClick
                ? {
                    onClick: (e) => {
                        if (suppressClick.current) {
                            e.preventDefault();
                            return;
                        }
                        onEventClick(ev);
                    },
                }
                : {}) }));
        return renderEvent ? renderEvent(ev, chip) : chip;
    };
    return (_jsx("div", { ref: setRefs, role: "group", ...rest, className: cx('mn-time-grid', className), style: vars, "data-multi": multi || undefined, "data-dragging": drag ? '' : undefined, children: _jsxs("div", { className: "mn-time-grid__inner", children: [_jsxs("div", { className: "mn-time-grid__head", children: [_jsx("div", { className: "mn-time-grid__corner", "aria-hidden": true }), days.map((day) => {
                            const isToday = day === today;
                            const content = (_jsxs(_Fragment, { children: [_jsx("span", { className: "mn-time-grid__weekday", children: formatDay(day, { weekday: 'short' }, locale) }), ' ', _jsx("span", { className: "mn-time-grid__date", children: formatDay(day, { day: 'numeric' }, locale) })] }));
                            return (_jsx("div", { className: "mn-time-grid__day-head", "data-today": isToday || undefined, children: multi && onDayClick ? (_jsx("button", { type: "button", className: "mn-time-grid__day-button", "aria-label": `${formatDay(day, { weekday: 'short' }, locale)} ${formatDay(day, { day: 'numeric' }, locale)}, ${openDayLabel(longDay(day))}`, "aria-current": isToday ? 'date' : undefined, onClick: () => onDayClick(day), children: content })) : (_jsxs("span", { className: "mn-time-grid__day-label", children: [_jsx("span", { className: "mn-time-grid__day-short", "aria-hidden": true, children: content }), _jsx("span", { className: "mn-visually-hidden", children: isToday ? `${longDay(day)}, ${todayLabel}` : longDay(day) })] })) }, day));
                        }), hasAllDay ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "mn-time-grid__all-day-label", "aria-hidden": true, children: allDayLabel }), columns.map(({ day, allDay }) => (_jsx("div", { className: "mn-time-grid__all-day", role: "group", "aria-label": `${longDay(day)}, ${allDayLabel}`, children: allDay.map((ev) => (_jsx(Fragment, { children: chipFor(ev, 'bar', 0) }, ev.id))) }, day)))] })) : null] }), _jsxs("div", { className: "mn-time-grid__body", ref: bodyRef, children: [_jsx("div", { className: "mn-time-grid__gutter", "aria-hidden": true, children: hours.map((h, i) => i === 0 ? null : (_jsx("span", { className: "mn-time-grid__hour", style: { top: (h * 60 - rangeStart) * minutePx }, children: formatMinutes(h * 60, locale, hc) }, h))) }), _jsx("div", { className: "mn-time-grid__columns", children: columns.map(({ day, timed, off, shade }, dayIndex) => {
                                const isToday = day === today;
                                const ghost = drag && drag.dayIndex === dayIndex ? drag : null;
                                return (_jsxs("div", { className: "mn-time-grid__column", role: "group", "aria-label": longDay(day), "data-today": isToday || undefined, "data-creatable": onSlotClick ? '' : undefined, children: [shade.map((s) => (_jsx("div", { className: "mn-time-grid__shade", "aria-hidden": true, style: {
                                                top: (s.top - rangeStart) * minutePx,
                                                height: (s.bottom - s.top) * minutePx,
                                            } }, `shade-${s.top}`))), off.map(({ u, top, bottom }) => (_jsx("div", { className: "mn-time-grid__unavailable", style: {
                                                top: (top - rangeStart) * minutePx,
                                                height: (bottom - top) * minutePx,
                                            }, children: u.label ? (_jsx("span", { className: "mn-time-grid__unavailable-label", children: u.label })) : null }, u.id))), onSlotClick
                                            ? hours.map((h) => {
                                                const isActive = active.dayIndex === dayIndex && active.hour === h;
                                                const when = `${longDay(day)}, ${formatMinutes(h * 60, locale, hc)}`;
                                                return (_jsx("button", { type: "button", className: "mn-time-grid__slot", "data-slot": `${dayIndex}:${h}`, tabIndex: isActive ? 0 : -1, "aria-label": createLabel(when), style: { top: (h * 60 - rangeStart) * minutePx }, onFocus: () => setActiveSlot({ dayIndex, hour: h }), onKeyDown: (e) => onSlotKeyDown(e, dayIndex, h), onClick: (e) => onSlotClick(slotFromPointer(e, day, h)) }, h));
                                            })
                                            : null, timed.map(({ item: ev, top, bottom, col, cols }) => {
                                            const heightPx = (bottom - top) * minutePx;
                                            return (_jsx("div", { className: "mn-time-grid__event", "data-movable": movable(ev) || undefined, "data-ghosted": drag?.event.id === ev.id || undefined, style: {
                                                    top: (top - rangeStart) * minutePx,
                                                    height: Math.max(heightPx, 18),
                                                    '--mn-time-grid-col': String(col),
                                                    '--mn-time-grid-cols': String(cols),
                                                }, onPointerDown: (e) => onEventPointerDown(e, ev, top), onPointerMove: (e) => onEventPointerMove(e, ev), onPointerUp: onEventPointerUp, onPointerCancel: cancelDrag, onClickCapture: (e) => {
                                                    if (suppressClick.current) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                }, children: chipFor(ev, 'block', heightPx) }, ev.id));
                                        }), ghost ? (_jsxs("div", { className: "mn-time-grid__ghost", "aria-hidden": true, style: {
                                                top: (ghost.startMin - rangeStart) * minutePx,
                                                height: Math.max(ghost.duration * minutePx, 18),
                                                ...(ghost.event.color ? { '--mn-event-color': ghost.event.color } : {}),
                                            }, children: [formatMinutes(ghost.startMin, locale, hc), " \u00B7 ", ghost.event.title] })) : null, isToday &&
                                            nowParts &&
                                            nowParts.minutes >= rangeStart &&
                                            nowParts.minutes < rangeEnd ? (_jsx("div", { className: "mn-time-grid__now", "aria-hidden": true, style: { top: (nowParts.minutes - rangeStart) * minutePx } })) : null] }, day));
                            }) })] })] }) }));
});
//# sourceMappingURL=time-grid.js.map